// src/components/ProgressiveImage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Image, View } from '@aws-amplify/ui-react';
import { loadPhotoUrl, loadPhotoThumbnailUrl } from '../../services/enhancedPhotoUrlService';

interface ProgressiveImageProps {
  photoId: string;
  alt: string;
  className?: string;
  width?: string;
  height?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  loadFullImage?: boolean;
  useBlurTransition?: boolean; // New prop to control blur effect
  onLoad?: () => void;
}

/**
 * A component that progressively loads images - first thumbnail, then full image
 */
const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  photoId,
  alt,
  className = '',
  width = '100%',
  height = '100%',
  objectFit = 'cover',
  loadFullImage = false,
  useBlurTransition = false, // Default to false for grid view
  onLoad
}) => {
  const cacheKey = `photo-${photoId}`;

  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [thumbnailLoaded, setThumbnailLoaded] = useState<boolean>(false);
  const [fullImageLoaded, setFullImageLoaded] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Load thumbnail first
  useEffect(() => {
    let mounted = true;

    const loadThumbnail = async () => {
      try {
        setIsLoading(true);
        const url = await loadPhotoThumbnailUrl(photoId);
        if (mounted) {
          setThumbnailUrl(url);
        }
      } catch (error) {
        console.error(`Error loading thumbnail for ${photoId}:`, error);
      }
    };

    if (photoId) {
      loadThumbnail();
    }

    return () => { mounted = false; };
  }, [photoId]);

  // Load full image after thumbnail if requested
  useEffect(() => {
    let mounted = true;

    const loadFullImage = async () => {
      if (!loadFullImage || !thumbnailLoaded) return;

      try {
        const url = await loadPhotoUrl(photoId, false);
        if (mounted) {
          setFullImageUrl(url);
        }
      } catch (error) {
        console.error(`Error loading full image for ${photoId}:`, error);
      }
    };

    if (photoId && thumbnailLoaded) {
      loadFullImage();
    }

    return () => { mounted = false; };
  }, [photoId, thumbnailLoaded, loadFullImage]);

  // Handle thumbnail load completion
  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
    setIsLoading(false);
    if (!loadFullImage && onLoad) {
      onLoad();
    }
  };

  // Handle full image load completion
  const handleFullImageLoad = () => {
    setFullImageLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  if (!thumbnailUrl && isLoading) {
    return (
      <View
        width={width}
        height={height}
        className={`progressive-image-placeholder ${className}`}
      >
        <div className="loading-spinner"></div>
      </View>
    );
  }

  return (
    <div className="progressive-image-container" style={{ position: 'relative', width, height }}>
      {/* Thumbnail layer */}
      <Image
        key={`thumb-${cacheKey}`}
        ref={imgRef}
        src={thumbnailUrl || ''}
        alt={`${alt} (thumbnail)`}
        width={width}
        height={height}
        objectFit={objectFit}
        className={`progressive-image thumbnail ${useBlurTransition ? 'with-blur' : ''} ${fullImageLoaded ? 'fade-out' : ''} ${className}`}
        onLoad={handleThumbnailLoad}
        loading="eager" 
        style={{
          position: thumbnailLoaded && fullImageUrl ? 'absolute' : 'relative',
          top: 0,
          left: 0
        }}
        crossOrigin="anonymous"
      />

      {/* Full image layer - only shown when loaded */}
      {loadFullImage && thumbnailLoaded && fullImageUrl && (
        <Image
          key={`full-${cacheKey}`} // Add stable key for browser caching
          src={fullImageUrl}
          alt={alt}
          width={width}
          height={height}
          objectFit={objectFit}
          className={`progressive-image full ${fullImageLoaded ? 'fade-in' : 'hidden'} ${className}`}
          onLoad={handleFullImageLoad}
          loading="eager" // Force immediate loading
          crossOrigin="anonymous" // Enable browser caching for cross-origin resources
        />
      )}
    </div>
  );
};

export default ProgressiveImage;